"use client"
import { Button, ButtonGroup, Flex, useTheme, Image, useColorModeValue, Box, Text, Badge } from "@chakra-ui/react"
import {
  FaShoppingCart,
  FaClipboardList,
  FaMedkit,
  FaRegChartBar,
  FaChartPie,
  FaInfoCircle,
  FaDatabase,
} from "react-icons/fa"
import { useSupplierRetailer } from "../wrappers/SupplierRetailerContext"

const RetailerNavigation = () => {
  const theme = useTheme()
  const retailerLogos = { PNP: "/pnp.jpg", CHK: "/chk.jpg", MED: "/medirite.jpeg", PET: "/petshop.jpeg", DIS: "/dis.jpg", CLI: "/cli.jpg", MAK: "/mak.jpg" }
  const { selectedRetailer, setSelectedRetailer, retailers, availableRetailers } = useSupplierRetailer()

  // Color mode values
  const buttonBg = useColorModeValue("white", "gray.800")
  const buttonDisabledBg = useColorModeValue("gray.100", "gray.700")
  const activeBorderColor = useColorModeValue(theme.colors.primary1, theme.colors.primary2)
  const hoverBorderColor = useColorModeValue(theme.colors.primary2, theme.colors.primary1)
  const activeTextColor = useColorModeValue(theme.colors.primary1, theme.colors.primary2)
  const inactiveTextColor = useColorModeValue("gray.500", "gray.400")
  const containerBg = useColorModeValue("white", "gray.900")
  const shadowColor = useColorModeValue("rgba(0, 0, 0, 0.1)", "rgba(0, 0, 0, 0.4)")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const bg = useColorModeValue("gray.50", "gray.700")

  const handleRetailerChange = (retailer) => {
    if (availableRetailers[retailer]) {
      setSelectedRetailer(retailer)
    }
  }

  const iconMap = {
    PNP: <FaShoppingCart />,
    CHK: <FaClipboardList />,
    MED: <FaMedkit />,
    PET: <FaShoppingCart />,
    DIS: <FaMedkit />,
    MAK: <FaRegChartBar />,
    CLI: <FaChartPie />,
    SUMMARY: <FaInfoCircle />,
    DATA: <FaDatabase />,
  }

  return (
   <Box
      position="fixed"
      top="0"
      left="200px"
      right="0"
      width="auto"
      zIndex="1000"
      bg={containerBg}
      boxShadow="0 2px 10px rgba(0, 0, 0, 0.15)"
      py={3}
      px={6}
    >
      <Flex
        justify="flex-start"
        align="center"
        overflowX="auto"
        pb={2}
        css={{
          "&::-webkit-scrollbar": {
            height: "4px",
          },
          "&::-webkit-scrollbar-track": {
            height: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.2)",
            borderRadius: "24px",
          },
        }}
      >
        <ButtonGroup spacing="4" mb="0">
          {retailers.map((btn) => (
            <Button
              key={btn.value}
              onClick={() => handleRetailerChange(btn.value)}
              bg={availableRetailers[btn.value] ? buttonBg : buttonDisabledBg}
              border={selectedRetailer === btn.value ? `2px solid ${activeBorderColor}` : "1px solid transparent"}
              boxShadow={selectedRetailer === btn.value ? `0 4px 12px ${shadowColor}` : "md"}
              borderRadius="lg"
              _hover={{
                border: availableRetailers[btn.value] ? `1px solid ${hoverBorderColor}` : "1px solid transparent",
                cursor: availableRetailers[btn.value] ? "pointer" : "not-allowed",
                transform: availableRetailers[btn.value] ? "translateY(-2px)" : "none",
                boxShadow: availableRetailers[btn.value] ? `0 6px 20px ${activeBorderColor}33` : "md",
                "& .retailer-logo": {
                  transform: "scale(1.1)",
                },
              }}
              _active={{
                transform: availableRetailers[btn.value] ? "scale(0.96)" : "none",
                boxShadow: availableRetailers[btn.value] ? `0 2px 8px ${shadowColor}` : "md",
              }}
              color={selectedRetailer === btn.value ? activeTextColor : inactiveTextColor}
              overflow="hidden"
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              isDisabled={!availableRetailers[btn.value]}
              opacity={availableRetailers[btn.value] ? 1 : 0.6}
              py={3}
              px={4}
              minW="150px"
              transition="all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
              position="relative"
            >
              <Flex align="center" width="100%">
                <Box
                  className="retailer-logo"
                  borderRadius="full"
                  overflow="hidden"
                  mr={3}
                  boxSize="44px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="1px solid"
                  borderColor={borderColor}
                  bg={bg}
                  p={1}
                  transition="transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
                >
                  <Image
                    width="100%"
                    height="100%"
                    src={retailerLogos[btn.value] || "/placeholder.svg"}
                    alt={btn.text}
                    objectFit="contain"
                    fallback={iconMap[btn.value]}
                  />
                </Box>
                <Text fontWeight={selectedRetailer === btn.value ? "bold" : "medium"}>{btn.text}</Text>
                {selectedRetailer === btn.value && (
                  <Badge
                    position="absolute"
                    top="0"
                    right="0"
                    bg={activeBorderColor}
                    color="white"
                    fontSize="xs"
                    borderBottomLeftRadius="md"
                    px={2}
                    py={0.5}
                    sx={{
                      animation: "badgePulse 2s ease-in-out infinite",
                      "@keyframes badgePulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.7 },
                      },
                    }}
                  >
                    Active
                  </Badge>
                )}
              </Flex>
            </Button>
          ))}
        </ButtonGroup>
      </Flex>
    </Box>
  )
}

export default RetailerNavigation
